import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  ColorInput,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';

export type ScheduleCategory = {
  id: string;
  form_id: string;
  name: string;
  color: string;
  icon: string | null;
  is_default: boolean;
  sort_order: number;
};

type Props = {
  formId: string;
  editCategoryId?: string | null;
  onEditCategoryHandled?: () => void;
};

export function AblaufplanCategoryManager({ formId, editCategoryId, onEditCategoryHandled }: Props) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleCategory | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366F1');
  const [icon, setIcon] = useState('');

  const categoriesQ = useQuery({
    queryKey: ['schedule-categories', formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schedule_categories')
        .select('*')
        .eq('form_id', formId)
        .order('sort_order');
      if (error) throw error;
      return data as ScheduleCategory[];
    },
  });

  const openCreate = () => {
    setEditing(null);
    setName('');
    setColor('#6366F1');
    setIcon('');
    setModalOpen(true);
  };

  const openEdit = (cat: ScheduleCategory) => {
    setEditing(cat);
    setName(cat.name);
    setColor(cat.color);
    setIcon(cat.icon ?? '');
    setModalOpen(true);
  };

  useEffect(() => {
    if (!editCategoryId || !categoriesQ.data) return;
    const target = categoriesQ.data.find((cat) => cat.id === editCategoryId);
    if (!target) return;
    openEdit(target);
    onEditCategoryHandled?.();
  }, [editCategoryId, categoriesQ.data, onEditCategoryHandled]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name ist erforderlich');
      if (editing) {
        const { error } = await supabase
          .from('schedule_categories')
          .update({ name: name.trim(), color, icon: icon.trim() || null })
          .eq('id', editing.id);
        if (error) throw error;
      } else {
        const maxOrder = Math.max(
          0,
          ...(categoriesQ.data ?? []).map((c) => c.sort_order),
        );
        const { error } = await supabase.from('schedule_categories').insert({
          form_id: formId,
          name: name.trim(),
          color,
          icon: icon.trim() || null,
          sort_order: maxOrder + 1,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-categories', formId] });
      setModalOpen(false);
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (cat: ScheduleCategory) => {
      // Check if any entries use this category
      const { count, error: countErr } = await supabase
        .from('schedule_entries')
        .select('id', { count: 'exact', head: true })
        .eq('category_id', cat.id);
      if (countErr) throw countErr;
      if ((count ?? 0) > 0) {
        const confirmed = window.confirm(
          `Diese Kategorie wird von ${count} Eintrag/Einträgen verwendet. Trotzdem löschen? Die Einträge verlieren ihre Kategorie.`,
        );
        if (!confirmed) return;
      }
      const { error } = await supabase
        .from('schedule_categories')
        .delete()
        .eq('id', cat.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-categories', formId] });
      qc.invalidateQueries({ queryKey: ['schedule-entries', formId] });
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  return (
    <>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Kategorien koennen hier erstellt, umbenannt und geloescht werden.
          </Text>
          <Button size="xs" variant="light" onClick={openCreate}>
            + Kategorie
          </Button>
        </Group>

        {(categoriesQ.data ?? []).map((cat) => (
          <Group
            key={cat.id}
            justify="space-between"
            wrap="nowrap"
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'color-mix(in srgb, var(--mantine-color-dark-6) 65%, transparent)',
            }}
          >
            <Badge
              color={cat.color}
              variant="filled"
              style={{ maxWidth: '100%', cursor: 'pointer' }}
              onClick={() => openEdit(cat)}
            >
              {cat.icon ? `${cat.icon} ` : ''}{cat.name}
            </Badge>
            <Group gap={6} wrap="nowrap">
              <Button size="compact-xs" variant="subtle" onClick={() => openEdit(cat)}>
                Umbenennen
              </Button>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                onClick={() => deleteMutation.mutate(cat)}
                title="Löschen"
              >
                ×
              </ActionIcon>
            </Group>
          </Group>
        ))}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Kategorie bearbeiten' : 'Kategorie erstellen'}
        size="sm"
      >
        <Stack>
          <TextInput
            label="Name"
            placeholder="z.B. Chor, Tanz, Technik"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <ColorInput
            label="Farbe"
            value={color}
            onChange={setColor}
            format="hex"
          />
          <TextInput
            label="Icon (optional)"
            placeholder="z.B. 🎵 oder Emoji"
            value={icon}
            onChange={(e) => setIcon(e.currentTarget.value)}
            description="Optionales Emoji oder Zeichen"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
            >
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
